/**
 * Binds the pure engine to the live app: reads history and profile out of
 * `AppState`, calls `prescribeNextSession`, hands back a result the UI can
 * render.
 *
 * **Why there is no `store.js`.** The plan called for an
 * `exercise_progressions` table "treated as a cache of a pure function of
 * `sets`". Having built the engine, that cache has no job to do:
 * `sbLoadWorkouts` already pulls every set of every workout into memory at
 * sign-in, and the engine is a sub-millisecond pure function over that array.
 * A cache table would add a migration, a write path on every workout save, and
 * an invalidation bug surface — to avoid arithmetic that costs nothing. It also
 * could not be trusted more than the source it derives from.
 *
 * The part of that plan item that *does* earn its keep is the other half:
 * `sets.prescribed_weight_kg / prescribed_reps / prescription_engine_version`.
 * That data is not derivable — it records what we told the user to do, which is
 * how a stall gets told apart from non-adherence and how the thresholds get
 * tuned. It needs a migration and is not in this change.
 */

import { legacy } from '../../runtime/legacy.js';
import { prescribeNextSession } from './engine.js';
import { getExerciseHistory, getExercise, getWeeklySetsByMuscleGroup } from './history.js';
import { getLandmarks } from './volume.js';

/** How far back the engine looks. Enough for the 3-session rules, not so far that a training block from last year votes. */
const HISTORY_WINDOW = 8;

/**
 * @param {string} exerciseId
 * @param {object} [opts]
 * @param {string} [opts.muscleGroup] Falls back to the exercise catalogue.
 * @returns {object} the `prescribeNextSession` result
 */
export function prescribeFor(exerciseId, { muscleGroup } = {}) {
  const AppState = legacy('AppState');
  const exercise = getExercise(exerciseId);
  const group = muscleGroup || exercise?.muscle_group || null;

  const sessions = getExerciseHistory(exerciseId, { limit: HISTORY_WINDOW });
  const weeklySets = getWeeklySetsByMuscleGroup();

  return prescribeNextSession({
    goal: AppState?.profile?.goal || 'general',
    equipment: (exercise?.equipment || '').toLowerCase() || 'other',
    // `exercises.exercise_type` is what keeps a strength-goal user off 3-rep
    // cable crossovers. Absent (a custom exercise saved without one) it falls
    // through to the column's own default, compound.
    exerciseType: (exercise?.exercise_type || '').toLowerCase() || 'compound',
    muscleGroup: group,
    unitSystem: AppState?.unitPref === 'imperial' ? 'imperial' : 'metric',
    sessions,
    weeklySetsForMuscleGroup: group ? weeklySets[group] : undefined,
    volumeLandmarks: group ? getLandmarks(group) : null,
  });
}
